
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Event } from "@/lib/types"; // Assuming Event type is compatible or can be adjusted
import { useEffect } from "react";
import { useToast } from "@/hooks/use-toast";
import { EventLoadingState } from "@/components/event/EventLoadingState";
import { EventErrorState } from "@/components/event/EventErrorState";
import { EventHeader } from "@/components/event/EventHeader";
import { EventTicketCTA } from "@/components/event/EventTicketCTA";
import { EventDetailsContent } from "@/components/event/EventDetailsContent";
import { api } from '@/components/client/backend';

const EventOverview = () => {
  const { seoUrl } = useParams();
  const { toast } = useToast();

  const { data: event, isLoading, error } = useQuery<Event | undefined>({
    queryKey: ["event", seoUrl],
    queryFn: async () => {
      if (!seoUrl) {
        throw new Error("Event URL is required");
      }

      try {
        // Attempt to fetch events and filter by seo_url
        const event = await api(`events/${encodeURI(seoUrl)}`).json<Event>();

        if (!event) {
          throw new Error("Event not found");
        }

        return event;
      } catch (err) {
        console.error("Error fetching event:", err);
        throw new Error("Unable to load event details.");
      }
    },
    retry: 1,
    enabled: !!seoUrl,
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
  });

  useEffect(() => {
    if (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message, // Use the error message from the queryFn
      });
    }
  }, [error, toast]);

  if (isLoading) {
    return <EventLoadingState />;
  }

  // Check for error or if event is explicitly undefined after fetch
  if (error || event === undefined) {
    return <EventErrorState />;
  }

  return (
    <div className="min-h-screen bg-black flex flex-col">
      <Header />
      <main className="flex-grow mx-auto px-4 pt-32 relative z-10 max-w-3xl w-full">
        <div className="w-full aspect-video mb-8 rounded-lg overflow-hidden shadow-lg">
          <img
            src={event.imageUrl || "/placeholder.svg"}
            alt={event.title}
            className="w-full h-full object-cover"
          />
        </div>

        <EventHeader event={event} />
        <EventTicketCTA event={event} />
        <EventDetailsContent event={event} />
      </main>
      <Footer />
    </div>
  );
};

export default EventOverview;
